import { ERROR_SPECS, ErrorCode, type Permission } from "@sarva/domain-types";
import { isSarvaApiError, type Problem, type ProblemFieldError } from "@sarva/api-client";
import { getLastRetryAfterSeconds } from "./client";

/**
 * (H) doc 27 §6: errors are handled by `code` from the generated `ErrorCode`
 * union — never by parsing a message.
 *
 * This module is the single place that turns a failure into a *decision*. Call
 * sites switch on `view.kind`; they never look at a status number, and they
 * never look at a string. The mapping from code to kind runs through
 * `ERROR_SPECS[code].status`, which is generated from `contracts/errors.yaml`,
 * so a new error code lands in the right branch the day it is added rather
 * than the day somebody remembers to update a `switch`.
 *
 * The HTTP status is used only as the fallback for a response that did not
 * come from this API at all — a proxy error page, a dropped connection — where
 * there is no code to branch on.
 */

/** What the UI must do. One member per row of doc 27 §6's table. */
export type ProblemView =
  /** `401` — refresh, then re-authenticate preserving the current location. */
  | { kind: "unauthenticated"; code: ErrorCode; title: string; detail: string }
  /** `403` — inline explanation naming what to request. Never a blank screen. */
  | {
      kind: "forbidden";
      code: ErrorCode;
      title: string;
      detail: string;
      /** Named in the problem's `meta.permission` when the API says which. */
      missing: readonly Permission[];
    }
  /** `404` — not-found state inside the current layout, with a way back. */
  | { kind: "not-found"; code: ErrorCode; title: string; detail: string; resourceType?: string }
  /**
   * `409` / `412` / `428` — a conflict dialog showing what changed, offering
   * reload. (H) Never a silent overwrite.
   */
  | {
      kind: "conflict";
      code: ErrorCode;
      title: string;
      detail: string;
      expectedVersion?: number;
      actualVersion?: number;
      /**
       * The sentence naming *what* collided, from the problem's `meta.detail`.
       *
       * `ALREADY_EXISTS` renders its top-level `detail` from the generated
       * template — "A patient with that identifier already exists" — which is
       * the same sentence for every conflict in the product. The router that
       * raised it passes the specific one as `detail=`, which the API puts in
       * `meta.detail`: "Another patient at this practice already holds that
       * medical record number." That is the half a person can act on, and it
       * was being dropped.
       *
       * (H) It names the **field** and never the value. Echoing a patient
       * identifier back into an error body is still echoing patient data, which
       * is why the API writes these sentences the way it does — and why this
       * renders one verbatim rather than composing its own from the request.
       */
      specifics?: string;
    }
  /** `422` — field errors mapped from `errors[]`, plus a form-level message. */
  | {
      kind: "validation";
      code: ErrorCode;
      title: string;
      detail: string;
      fields: readonly ProblemFieldError[];
    }
  /** `429` — backoff with a visible retry timer. */
  | { kind: "rate-limited"; code: ErrorCode; title: string; detail: string; retryAfterSeconds: number }
  /** `5xx` — the error boundary, showing `request_id` for support. */
  | { kind: "server"; code: ErrorCode; title: string; detail: string; requestId: string | null }
  /** Anything else, including a response that was never a problem document. */
  | { kind: "unknown"; code: ErrorCode; title: string; detail: string; requestId: string | null };

const DEFAULT_RETRY_AFTER_SECONDS = 30;

/** Reads a `meta` member without asserting the whole document's shape. */
function metaValue(problem: Problem | null, key: string): unknown {
  return problem?.meta?.[key];
}

function metaNumber(problem: Problem | null, key: string): number | undefined {
  const value = metaValue(problem, key);
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return undefined;
}

function metaString(problem: Problem | null, key: string): string | undefined {
  const value = metaValue(problem, key);
  return typeof value === "string" ? value : undefined;
}

/** True when the value is a code this build knows about. */
export function isKnownErrorCode(code: string): code is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_SPECS, code);
}

/**
 * The status the *contract* gives a code, which is what classification uses.
 * Falls back to the transport status only when the code is unknown, i.e. when
 * the failure did not come from this API.
 */
function contractStatus(code: string, transportStatus: number): number {
  return isKnownErrorCode(code) ? ERROR_SPECS[code].status : transportStatus;
}

interface Parsed {
  code: ErrorCode;
  title: string;
  detail: string;
  status: number;
  requestId: string | null;
  problem: Problem | null;
  fields: readonly ProblemFieldError[];
}

/** The placeholder the generated catalogue uses; `errors.ts` is vendored, so it is matched, not edited. */
const REQUEST_ID_TOKEN = "{request_id}";

/**
 * Put the request id into a detail that asks for one.
 *
 * (H) `INTERNAL_ERROR` is the one catalogue entry whose `detailTemplate` carries a
 * `{request_id}` placeholder, and `parse` handed that template straight out as `detail`.
 * Two call sites happened to substitute it themselves — `error-boundary.tsx` and
 * `DataTableStates.tsx` — and every other screen rendered the literal text "Quote reference
 * {request_id} if you contact support." to the user. Reported from the hosted demo,
 * 2026-10-13.
 *
 * Doing it here means a screen cannot forget: `ProblemView.detail` is always a finished
 * sentence, and the two call sites' own `.replace` calls become harmless no-ops.
 *
 * With no id to quote, the sentence asking the reader to quote one is **removed** rather
 * than filled with a placeholder, "unknown", or an empty gap. A failure that never reached
 * the API — `fetch` throwing on a dead network — has no request id anywhere, and telling
 * someone to quote a reference that does not exist sends them to support with nothing.
 */
function withRequestId(template: string, requestId: string | null): string {
  if (!template.includes(REQUEST_ID_TOKEN)) return template;
  if (requestId) return template.replaceAll(REQUEST_ID_TOKEN, requestId);
  return template
    .replace(/\s*Quote reference\s+\{request_id\}[^.]*\.\s*/u, " ")
    .replaceAll(REQUEST_ID_TOKEN, "")
    .replace(/\s{2,}/gu, " ")
    .trim();
}

function parse(error: unknown): Parsed {
  if (!isSarvaApiError(error)) {
    // Not an API failure at all: a thrown TypeError from `fetch` (offline, DNS,
    // CORS) or a bug in a component. Both are "something went wrong on our
    // side" as far as the person looking at the screen is concerned.
    const spec = ERROR_SPECS[ErrorCode.INTERNAL_ERROR];
    return {
      code: ErrorCode.INTERNAL_ERROR,
      title: spec.title,
      detail: withRequestId(spec.detailTemplate, null),
      status: spec.status,
      requestId: null,
      problem: null,
      fields: [],
    };
  }

  const problem = (error.problem ?? null) as Problem | null;
  const known = isKnownErrorCode(error.code);
  const code: ErrorCode = known ? (error.code as ErrorCode) : ErrorCode.INTERNAL_ERROR;
  const spec = ERROR_SPECS[code];

  return {
    code,
    // The title always comes from the generated catalogue, so the heading a
    // user sees for a given code is identical everywhere in the product.
    title: spec.title,
    // (H) doc 22 §4: `detail` is written to be shown to a user and never
    // carries a trace, SQL or PHI. Using it is not "parsing a message" — the
    // branch above was already taken on `code`.
    detail: withRequestId(
      problem?.detail ?? spec.detailTemplate,
      problem?.request_id ?? error.requestId,
    ),
    status: contractStatus(error.code, error.status),
    requestId: problem?.request_id ?? error.requestId,
    problem,
    fields: problem?.errors ?? [],
  };
}

/** Turn any caught value into the one decision the UI has to make. */
export function describeProblem(error: unknown): ProblemView {
  const p = parse(error);

  switch (p.status) {
    case ERROR_SPECS[ErrorCode.UNAUTHENTICATED].status:
      return { kind: "unauthenticated", code: p.code, title: p.title, detail: p.detail };

    case ERROR_SPECS[ErrorCode.PERMISSION_DENIED].status: {
      const named = metaString(p.problem, "permission");
      return {
        kind: "forbidden",
        code: p.code,
        title: p.title,
        detail: p.detail,
        // The API names the permission it wanted in `meta.permission`. It is
        // deliberately not widened to `Permission` by assertion: an unknown
        // string is simply not shown as a permission to request.
        missing: named === undefined ? [] : ([named] as readonly Permission[]),
      };
    }

    case ERROR_SPECS[ErrorCode.NOT_FOUND].status: {
      const resourceType = metaString(p.problem, "resource_type");
      return {
        kind: "not-found",
        code: p.code,
        title: p.title,
        detail: p.detail,
        ...(resourceType === undefined ? {} : { resourceType }),
      };
    }

    case ERROR_SPECS[ErrorCode.ALREADY_EXISTS].status: // 409
    case ERROR_SPECS[ErrorCode.PRECONDITION_FAILED].status: // 412
    case ERROR_SPECS[ErrorCode.PRECONDITION_REQUIRED].status: {
      // 428
      const expected = metaNumber(p.problem, "expected");
      const actual = metaNumber(p.problem, "actual");
      // Not read for a `412`/`428`, where the versions above are the specifics;
      // present for a `409`, where the constraint that refused is.
      const specifics = metaString(p.problem, "detail");
      return {
        kind: "conflict",
        code: p.code,
        title: p.title,
        detail: p.detail,
        ...(expected === undefined ? {} : { expectedVersion: expected }),
        ...(actual === undefined ? {} : { actualVersion: actual }),
        ...(specifics === undefined || specifics === p.detail ? {} : { specifics }),
      };
    }

    case ERROR_SPECS[ErrorCode.VALIDATION_FAILED].status: // 422
      return {
        kind: "validation",
        code: p.code,
        title: p.title,
        detail: p.detail,
        fields: p.fields,
      };

    case ERROR_SPECS[ErrorCode.RATE_LIMITED].status: // 429
      return {
        kind: "rate-limited",
        code: p.code,
        title: p.title,
        detail: p.detail,
        // In order of authority: the problem document's own `meta`, then the
        // `Retry-After` header doc 22 §11 requires on a `429`, then a
        // conservative default. Never zero — a timer that says "now" is an
        // invitation to hammer the endpoint the limit is protecting.
        retryAfterSeconds:
          metaNumber(p.problem, "retry_after") ??
          getLastRetryAfterSeconds() ??
          DEFAULT_RETRY_AFTER_SECONDS,
      };

    default:
      if (p.status >= ERROR_SPECS[ErrorCode.INTERNAL_ERROR].status) {
        return {
          kind: "server",
          code: p.code,
          title: p.title,
          detail: p.detail,
          requestId: p.requestId,
        };
      }
      return {
        kind: "unknown",
        code: p.code,
        title: p.title,
        detail: p.detail,
        requestId: p.requestId,
      };
  }
}

/**
 * `errors[]` reshaped for React Hook Form's `setError`. (H) doc 27 §6: a `422`
 * lands on the field it belongs to, not in a banner the user has to translate.
 */
export function fieldErrorMap(view: ProblemView): ReadonlyMap<string, string> {
  if (view.kind !== "validation") return new Map();
  const map = new Map<string, string>();
  for (const entry of view.fields) {
    // First wins: the API lists the most specific failure first, and stacking
    // two messages under one input helps nobody.
    if (!map.has(entry.field)) map.set(entry.field, entry.detail);
  }
  return map;
}
